import React from 'react';
import { Filter, Plane, Sunrise, Sun, CloudSun, Sunset, Moon, MapPin } from 'lucide-react';

const FilterSidebar = ({
  selectedStops,
  setSelectedStops,
  selectedAirlines,
  setSelectedAirlines,
  selectedDepartureTimes,
  setSelectedDepartureTimes,
  selectedArrivalTimes,
  setSelectedArrivalTimes,
  selectedAirports,
  setSelectedAirports,
  priceRange,
  setPriceRange,
  stopCounts,
  uniqueAirlines,
  uniqueAirports,
  airlineLogos = {}
}) => {
  const toggleStop = (stopId) => {
    if (selectedStops.includes(stopId)) {
      setSelectedStops(selectedStops.filter(s => s !== stopId));
    } else {
      setSelectedStops([...selectedStops, stopId]);
    }
  };

  const toggleAirline = (code) => {
    if (selectedAirlines.includes(code)) {
      setSelectedAirlines(selectedAirlines.filter(a => a !== code));
    } else {
      setSelectedAirlines([...selectedAirlines, code]);
    }
  };

  const toggleDepartureTime = (timeId) => {
    if (selectedDepartureTimes?.includes(timeId)) {
      setSelectedDepartureTimes(selectedDepartureTimes.filter(t => t !== timeId));
    } else {
      setSelectedDepartureTimes([...(selectedDepartureTimes || []), timeId]);
    }
  };

  const toggleArrivalTime = (timeId) => {
    if (selectedArrivalTimes?.includes(timeId)) {
      setSelectedArrivalTimes(selectedArrivalTimes.filter(t => t !== timeId));
    } else {
      setSelectedArrivalTimes([...(selectedArrivalTimes || []), timeId]);
    }
  };

  const toggleAirport = (airportCode) => {
    if (selectedAirports?.includes(airportCode)) {
      setSelectedAirports(selectedAirports.filter(a => a !== airportCode));
    } else {
      setSelectedAirports([...(selectedAirports || []), airportCode]);
    }
  };

  const timeSlots = [
    { id: 'earlyMorning', label: 'Early Morning', time: '00:00 - 06:00', icon: Moon },
    { id: 'morning', label: 'Morning', time: '06:00 - 12:00', icon: Sunrise },
    { id: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00', icon: Sun },
    { id: 'evening', label: 'Evening', time: '18:00 - 24:00', icon: Sunset }
  ];

  return (
    <>
      {/* Custom scrollbar styles */}
      <style>{`
        .filter-sidebar-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .filter-sidebar-scroll::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 3px;
        }
        .filter-sidebar-scroll::-webkit-scrollbar-thumb {
          background: #659A9B;
          border-radius: 3px;
        }
        .filter-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #557E7F;
        }
      `}</style>
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 sticky top-20 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
      {/* Filter header - Fixed at top */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#659A9B]/10 flex items-center justify-center">
            <Filter className="w-3.5 h-3.5 text-[#659A9B]" />
          </div>
          <span className="text-sm font-bold text-[#1B375B]">Filters</span>
        </div>
        {(selectedStops.length > 0 || selectedAirlines.length > 0 || selectedDepartureTimes?.length > 0 || selectedArrivalTimes?.length > 0 || selectedAirports?.length > 0) && (
          <button
            onClick={() => {
              setSelectedStops([]);
              setSelectedAirlines([]);
              setSelectedDepartureTimes?.([]);
              setSelectedArrivalTimes?.([]);
              setSelectedAirports?.([]);
              setPriceRange([5000, 70000]);
            }}
            className="text-[10px] text-[#659A9B] font-bold uppercase tracking-wider hover:text-[#1B375B] transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Scrollable content area */}
      <div className="p-5 space-y-5 overflow-y-auto flex-1 filter-sidebar-scroll"
        style={{ 
          scrollbarWidth: 'thin',
          scrollbarColor: '#659A9B #E5E7EB'
        }}
      >
        {/* Stops */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Stops</p>
          <div className="flex gap-2">
            {[
              { id: 'nonStop', label: 'Direct', count: stopCounts.nonStop },
              { id: '1stop', label: '1 Stop', count: stopCounts.oneStop },
              { id: '2stop', label: '2+ Stops', count: stopCounts.twoPlus },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => toggleStop(s.id)}
                className={`flex-1 py-2.5 rounded-xl text-center text-xs font-semibold transition-all ${
                  selectedStops.includes(s.id)
                    ? 'bg-[#659A9B]/10 text-[#1B375B] ring-2 ring-[#659A9B]/40'
                    : 'bg-[#E7EEEE]/60 text-gray-500 hover:bg-[#E7EEEE]'
                }`}
              >
                <span className="block">{s.label}</span>
                <span className="block text-[10px] font-normal mt-0.5 opacity-50">{s.count} flights</span>
              </button>
            ))}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Price Range */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Price Range</p>
          <div className="bg-[#E7EEEE]/40 rounded-xl p-3">
            <input
              type="range"
              min="5000"
              max="70000"
              step="1000"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
              className="w-full accent-[#659A9B]"
            />
            <div className="flex justify-between text-[11px] mt-1.5">
              <span className="text-gray-400">₹{priceRange[0].toLocaleString()}</span>
              <span className="font-bold text-[#1B375B]">₹{priceRange[1].toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Departure Time */}
        {setSelectedDepartureTimes && (
          <>
            <div>
              <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Departure Time</p>
              <div className="grid grid-cols-2 gap-2">
                {timeSlots.map((slot) => {
                  const isSelected = selectedDepartureTimes?.includes(slot.id);
                  const IconComponent = slot.icon;
                  return (
                    <button
                      key={slot.id}
                      onClick={() => toggleDepartureTime(slot.id)}
                      className={`p-2.5 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                          : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                        <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                          {slot.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-400">{slot.time}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="h-[1px] bg-gray-100" />
          </>
        )}

        {/* Arrival Time */}
        {setSelectedArrivalTimes && (
          <>
            <div>
              <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Arrival Time</p>
              <div className="grid grid-cols-2 gap-2">
                {timeSlots.map((slot) => {
                  const isSelected = selectedArrivalTimes?.includes(slot.id);
                  const IconComponent = slot.icon;
                  return (
                    <button
                      key={slot.id}
                      onClick={() => toggleArrivalTime(slot.id)}
                      className={`p-2.5 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                          : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                        <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                          {slot.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-400">{slot.time}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="h-[1px] bg-gray-100" />
          </>
        )}

        {/* Airports */}
        {uniqueAirports && uniqueAirports.length > 0 && setSelectedAirports && (
          <>
            <div>
              <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Airports</p>
              <div className="space-y-1">
                {uniqueAirports.map((airport) => {
                  const isSelected = selectedAirports?.includes(airport.code);
                  return (
                    <button
                      key={airport.code}
                      onClick={() => toggleAirport(airport.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                        isSelected ? 'bg-[#659A9B]/10 ring-1 ring-[#659A9B]/30' : 'hover:bg-[#E7EEEE]/60'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border border-gray-100">
                        <MapPin className="w-3.5 h-3.5 text-[#659A9B]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs block ${isSelected ? 'font-bold text-[#1B375B]' : 'font-semibold text-gray-600'}`}>
                          {airport.code}
                        </span>
                        <span className="text-[10px] text-gray-400 truncate block">{airport.city}</span>
                      </div>
                      {isSelected && (
                        <div className="w-4 h-4 rounded-full bg-[#659A9B] flex items-center justify-center flex-shrink-0">
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="h-[1px] bg-gray-100" />
          </>
        )}

        {/* Airlines */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Airlines</p>
          <div className="space-y-1">
            {uniqueAirlines.slice(0, 10).map((a) => {
              const isSelected = selectedAirlines.includes(a.code);
              return (
                <button
                  key={a.code}
                  onClick={() => toggleAirline(a.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                    isSelected ? 'bg-[#659A9B]/10 ring-1 ring-[#659A9B]/30' : 'hover:bg-[#E7EEEE]/60'
                  }`}
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    {airlineLogos[a.code]?.square ? (
                      <img src={airlineLogos[a.code].square} alt={a.name} className="w-full h-full object-contain" />
                    ) : (
                      <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                    )}
                  </div>
                  <span className={`text-sm flex-1 ${isSelected ? 'font-semibold text-[#1B375B]' : 'text-gray-600'}`}>
                    {a.name}
                  </span>
                  {isSelected && (
                    <div className="w-4 h-4 rounded-full bg-[#659A9B] flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* End of scrollable content */}
    </div>
    {/* End of style wrapper */}
    </>
  );
};

export default FilterSidebar;
